import Container from "@/components/ui/Container";
import SectionHeader from "@/components/ui/SectionHeader";
import Card from "@/components/ui/Card";
import BulletList from "@/components/ui/BulletList";
import { UserAvatar, AssistantAvatar } from "./icons/ChatAvatars";
import { DISCLAIMER } from "./constants";
import useInView from "@/hooks/useInView";

const iconProps = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "#e85e57",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const ClockIcon = () => (
  <svg {...iconProps} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 16 14" />
  </svg>
);

const FlowIcon = () => (
  <svg {...iconProps} aria-hidden="true">
    <path d="M14 8V3H3v11h5" />
    <path d="M16 14h-8M13 17l3-3-3-3M21 21V8H8v13h13Z" />
  </svg>
);

const RefreshIcon = () => (
  <svg {...iconProps} aria-hidden="true">
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />
  </svg>
);

const cards = [
  {
    Icon: ClockIcon,
    title: "Быстрая диагностика инцидентов",
    text: "Инженеры не тратят время на сбор и интерпретацию данных — система показывает суть происходящего в 1 интерфейсе.",
    bullets: [
      "не нужно переключаться между инструментами",
      "снижается нагрузка на команду",
      "восстановление происходит быстрее",
    ],
  },
  {
    Icon: FlowIcon,
    title: "Переход от реакции к предупреждению",
    text: "Система показывает угрозы стабильности сервисов заранее. ИТ-команда действует до того, как возникают проблемы.",
    bullets: ["контролируются целевые показатели", "предотвращаются сбои"],
  },
  {
    Icon: RefreshIcon,
    title: "Связь ИТ с бизнес-результатом",
    text: "Становится понятно, как технические события влияют на выручку, конверсию и пользовательский опыт.",
    bullets: ["выделяется приоритет по рискам", "проще принимать решения"],
  },
];

const bodyText = "text-[16px] font-medium leading-[1.3] text-[#202020]";
const noteText = "text-[12px] font-medium leading-[1.3] text-[#999]";

function InfoCard({ Icon, title, text, bullets }) {
  return (
    <div className="flex flex-col">
      <Card variant="light" className="gap-6 md:min-h-[290px]">
        <span className="flex size-[44px] items-center justify-center rounded-[5px] bg-[#1c3564]">
          <Icon />
        </span>
        <h3 className="text-[21px] font-bold leading-none tracking-[-0.21px] text-[#1c3564]">
          {title}
        </h3>
        <p className={bodyText}>{text}</p>
      </Card>

      <div className="flex flex-col rounded-[25px] bg-surface px-[26px] py-[18px] md:min-h-[161px]">
        <BulletList className={bodyText} spacing="space-y-0" items={bullets} />
      </div>
    </div>
  );
}

function Chip({ label, value }) {
  return (
    <div className="flex h-[35px] items-center rounded-[5px] bg-[#171f2f] px-[13px]">
      <span className="text-[12px] font-medium leading-none">
        <span className="text-[#77aff1]">{label}</span>{" "}
        <span className="text-white">{value}</span>
      </span>
    </div>
  );
}

const bubbleText = "text-[12px] font-medium leading-[1.3] text-white";
const nameText = "text-[14px] font-bold leading-none text-white";
const timeText = "text-[12px] font-medium leading-none text-[#999]";
const avatarSize = "size-[44px]";

const bubbleBase =
  "transition-[opacity,transform] duration-500 ease-out motion-reduce:!translate-y-0 motion-reduce:!opacity-100 motion-reduce:transition-none";

function ChatMock() {
  const [ref, inView] = useInView();
  const revealed = inView
    ? "translate-y-0 opacity-100"
    : "translate-y-4 opacity-0";

  return (
    <div
      ref={ref}
      className="flex flex-col gap-[18px] rounded-[25px] bg-[#1c3564] p-[26px]"
    >
      {/* User message */}
      <div
        className={`flex flex-col gap-3 rounded-[5px] bg-[#001f40] p-[18px] ${bubbleBase} ${revealed}`}
      >
        <div className="flex items-center gap-3">
          <UserAvatar className={avatarSize} />
          <span className={nameText}>Вы</span>
          <span className={timeText}>11:46</span>
        </div>
        <p className={bubbleText}>
          Как повлияли вчерашние инциденты на выручку интернет-магазина, если
          учитывать, что средняя выручка в день 1 000 000 рублей?
        </p>
      </div>

      {/* Assistant message */}
      <div
        className={`flex flex-col gap-4 rounded-[5px] bg-[#202938] p-[18px] delay-[450ms] ${bubbleBase} ${revealed}`}
      >
        <div className="flex items-center gap-3">
          <AssistantAvatar className={avatarSize} />
          <span className={nameText}>Ассистент</span>
          <span className={timeText}>11:48</span>
        </div>
        <div className="flex flex-col gap-3">
          <p className={bubbleText}>
            Зафиксировано 2 инцидента общей длительностью 47 минут
          </p>
          <p className={bubbleText}>
            По расчету на основе конверсии и среднего чека — упущенная выручка
            составила ≈ 284 000 ₽
          </p>
          <p className={bubbleText}>
            Основная причина: деградация checkout-service в 11:20
          </p>
        </div>
        <div className="flex flex-col gap-[14px]">
          <Chip label="суммарный даунтайм" value="47 мин" />
          <Chip label="количество инцидентов" value="2" />
          <Chip label="конверсия в окне" value="-18%" />
        </div>
      </div>
    </div>
  );
}

export default function BusinessImpactSection() {
  return (
    <section>
      <Container>
        {/* TODO: Не вмещается в одну строку заголовок */}
        <SectionHeader
          label="Что получает бизнес и ИТ-команда"
          heading="Новый уровень предсказуемости"
        />

        <div className="mt-10 grid grid-cols-1 items-start gap-5 min-[478px]:mt-[60px] md:grid-cols-2 xl:mt-[84px] xl:grid-cols-4">
          <InfoCard {...cards[0]} />
          <InfoCard {...cards[1]} />

          <div className="flex flex-col">
            <InfoCard {...cards[2]} />
            <p
              className={`hidden md:mt-[80px] md:block xl:mt-[80px] ${noteText}`}
            >
              {DISCLAIMER}
            </p>
          </div>

          <div className="mx-auto w-full max-w-[324px] md:max-w-none">
            <ChatMock />
            {/* Disclaimer */}
            <p className={`mt-5 md:hidden ${noteText}`}>{DISCLAIMER}</p>
          </div>
        </div>
      </Container>
    </section>
  );
}
